/**
 * photo-utils.js — Foto-Upload / Delete / URL-Helpers für Item-Fotos.
 *
 * Fotos werden als JPEG in Cloudflare R2 gespeichert (über Worker).
 * Key-Schema: photos/${storeId}/${itemId}
 * Serve-URL:  /api/photos/${storeId}/${itemId}
 */

const MAX_DIMENSION = 800; // px
const JPEG_QUALITY  = 0.82;

/**
 * Komprimiert ein Bild auf max. MAX_DIMENSION px (longest edge), JPEG.
 * @param {File} file
 * @returns {Promise<Blob>}
 */
export async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Datei konnte nicht gelesen werden"));
    reader.onload = (ev) => {
      const img = new Image();
      img.onerror = () => reject(new Error("Bild konnte nicht geladen werden"));
      img.onload = () => {
        try {
          const { width, height } = img;
          const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
          const w = Math.round(width  * scale);
          const h = Math.round(height * scale);
          const canvas = document.createElement("canvas");
          canvas.width  = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) { reject(new Error("Canvas nicht verfügbar")); return; }
          ctx.drawImage(img, 0, 0, w, h);
          canvas.toBlob(blob => {
            if (!blob) { reject(new Error("canvas.toBlob lieferte null")); return; }
            resolve(blob);
          }, "image/jpeg", JPEG_QUALITY);
        } catch (err) {
          reject(err);
        }
      };
      img.src = ev.target.result; // data: URL — kein blob:, CSP-sicher
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Lädt ein Foto zu R2 hoch (via Worker POST /api/stores/:storeId/items/:itemId/photo).
 * Gibt { key } zurück.
 * @param {string} authToken
 * @param {string} storeId
 * @param {string} itemId
 * @param {Blob}   blob  — komprimiertes JPEG (compressImage-Output)
 * @returns {Promise<{ key: string }>}
 */
export async function uploadPhoto(authToken, storeId, itemId, blob) {
  const res = await fetch(`/api/stores/${storeId}/items/${itemId}/photo`, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${authToken}`,
      "Content-Type":  "image/jpeg",
    },
    body: blob,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || `Upload fehlgeschlagen (${res.status})`);
  }
  return res.json(); // { ok: true, key }
}

/**
 * Löscht ein Foto aus R2 (via Worker DELETE /api/stores/:storeId/items/:itemId/photo).
 * @param {string} authToken
 * @param {string} storeId
 * @param {string} itemId
 */
export async function deletePhoto(authToken, storeId, itemId) {
  const res = await fetch(`/api/stores/${storeId}/items/${itemId}/photo`, {
    method: "DELETE",
    headers: { "Authorization": `Bearer ${authToken}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error || `Löschen fehlgeschlagen (${res.status})`);
  }
}

/**
 * Gibt die Serve-URL für ein R2-Foto zurück.
 * key ist typischerweise "storeId/itemId" (ohne "photos/"-Prefix).
 * @param {string|null} imageKey  — Wert aus items.image_key
 * @returns {string|null}
 */
export function getPhotoUrl(imageKey) {
  if (!imageKey) return null;
  // Demo-Build: Fotos liegen lokal (Data-/Object-URLs), kein /api/photos-Request
  if (import.meta.env.VITE_DEMO === '1') return globalThis.__boothlyDemo?.photoUrl?.(imageKey) ?? null;
  // image_key kann "photos/storeId/itemId" sein → direkt als /api/photos/… servieren
  if (imageKey.startsWith("photos/")) {
    return `/api/${imageKey}`;
  }
  // Fallback für andere Keys (Labels etc.)
  return null;
}
